<template>
  <div class="pb-1">
    <div class="px-4 pt-2 pb-1.5">
      <span class="truncate text-lg font-medium">
        {{ currentUser?.fullName }}
      </span>
      <span class="-mt-0.5 truncate text-sm text-text-secondary">
        {{ currentUser?.email }}
      </span>
      <div
        v-if="isLabAdminUser"
        class="text-warning-text mt-2 flex items-center gap-1.5 rounded bg-warning-bg p-2 text-sm text-warning">
        <IconLucideCrown class="h-4 w-4 text-warning" />
        <span>{{ $t('term.lab-admin-user') }}</span>
      </div>

      <div
        v-if="isDeveloper"
        class="mt-2 flex items-center gap-2 text-sm text-text-secondary">
        <DevBadge />
        <span>{{ $t('W: Udvikler') }}</span>
      </div>
      <div
        v-if="
          isDeveloper &&
          (EMU.isEmulatingFunctions ||
            EMU.isEmulatingFirestore ||
            EMU.isEmulatingStorage ||
            EMU.isEmulatingPubsub)
        "
        class="mt-1.5 flex flex-col gap-1.5">
        <div
          v-if="EMU.isEmulatingFunctions"
          class="flex items-center gap-2 rounded bg-info-bg p-2 text-sm text-info">
          <IconLucideZap class="h-4 w-4 text-info" />
          <span>{{ $t('W: Functions-emulator aktiv') }}</span>
        </div>
        <div
          v-if="EMU.isEmulatingFirestore"
          class="flex items-center gap-2 rounded bg-info-bg p-2 text-sm text-info">
          <IconLucideDatabase class="h-4 w-4 text-info" />
          <span>{{ $t('W: Firestore-emulator aktiv') }}</span>
        </div>
        <div
          v-if="EMU.isEmulatingStorage"
          class="flex items-center gap-2 rounded bg-info-bg p-2 text-sm text-info">
          <IconLucideHardDrive class="h-4 w-4 text-info" />
          <span>{{ $t('W: Storage-emulator aktiv') }}</span>
        </div>
        <div
          v-if="EMU.isEmulatingPubsub"
          class="flex items-center gap-2 rounded bg-info-bg p-2 text-sm text-info">
          <IconLucideRadio class="h-4 w-4 text-info" />
          <span>{{ $t('W: Pub/Sub-emulator aktiv') }}</span>
        </div>
      </div>
    </div>
    <MenuDivider />
    <MenuOption
      id="profile"
      :title="$t('term.profile')"
      :isDisabled="isLabAdminUser"
      @select="onMenuSelect">
      <template #icon>
        <IconLucideUser
          class="-mt-1 h-4 w-4"
          aria-hidden="true" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="isLabAdminUser"
      id="update-pin"
      :title="$t('W: Opdater PIN-kode')"
      @select="onMenuSelect">
      <template #icon>
        <IconLucideKeyRound
          class="-mt-1 h-4 w-4"
          aria-hidden="true" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="canUser('employees.manage')"
      id="invite"
      :title="$t('action.invite-employees')"
      @select="onMenuSelect">
      <template #icon>
        <IconLucideMail
          class="-mt-1 h-4 w-4"
          aria-hidden="true" />
      </template>
    </MenuOption>
    <MenuDivider />
    <div
      v-if="isDeveloper"
      class="px-3 py-2">
      <div class="mb-1.5 flex items-center gap-2 px-1">
        <DevBadge />
        <span class="text-xs font-medium tracking-wide text-text-secondary">
          {{ $t('W: Udviklere') }}
        </span>
      </div>
      <RouterLink
        :to="{
          name: 'DesignSystemDocs',
          params: { labSlug: $route.params.labSlug },
        }"
        class="mt-1 flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-text-primary hover:bg-surface-muted"
        @click="$emit('close')">
        <IconLucidePanelRight
          class="h-4 w-4 text-text-secondary"
          aria-hidden="true" />
        {{ $t('W: Design System') }}
      </RouterLink>
      <RouterLink
        :to="{ name: 'Dev', params: { labSlug: $route.params.labSlug } }"
        class="mt-1 flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-text-primary hover:bg-surface-muted"
        @click="$emit('close')">
        <IconLucideCode2
          class="h-4 w-4 text-text-secondary"
          aria-hidden="true" />
        {{ $t('W: Dev indstillinger') }}
      </RouterLink>
      <button
        class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-text-primary hover:bg-surface-muted"
        @click="toggleShowDevActions">
        <span>{{ $t('W: Vis dev actions') }}</span>
        <div
          :class="[
            'relative h-5 w-9 rounded-full transition-colors',
            showDevActions ? 'bg-action' : 'bg-surface-sunken',
          ]">
          <div
            :class="[
              'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
              showDevActions ? 'translate-x-4.5' : 'translate-x-0.5',
            ]" />
        </div>
      </button>
      <div
        v-if="showDevActions"
        class="border-border-muted mt-1 ml-2 border-l pl-2">
        <!-- Catalog empty → seed it -->
        <button
          v-if="isCatalogEmpty"
          :disabled="isAnyOperationInProgress"
          class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
          @click="onSeedCatalogClick">
          <span>
            {{ isSeeding ? $t('W: Seeder katalog...') : $t('W: Seed katalog') }}
          </span>
          <IconLucideDatabase class="h-4 w-4" />
        </button>
        <!-- Catalog has data → seed orders + clear -->
        <template v-else>
          <button
            :disabled="isAnyOperationInProgress || !canSeedOrders"
            class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
            @click="onSeedOrdersClick">
            <span>
              {{
                isSeedingOrders
                  ? $t('W: Seeder ordrer...')
                  : $t('W: Seed ordrer')
              }}
            </span>
            <IconLucideShoppingCart class="h-4 w-4" />
          </button>
          <button
            v-if="hasOrders"
            :disabled="isAnyOperationInProgress"
            class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
            @click="onClearOrdersClick">
            <span>{{ $t('W: Slet ordrer') }}</span>
            <IconLucideTrash2 class="h-4 w-4" />
          </button>
          <button
            :disabled="isAnyOperationInProgress || !canSeedCustomerPricing"
            class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
            @click="onSeedCustomerPricingClick">
            <span>
              {{
                isSeedingCustomerPricing
                  ? $t('W: Seeder kundepriser...')
                  : $t('W: Seed kundepriser')
              }}
            </span>
            <IconLucideTags class="h-4 w-4" />
          </button>
          <button
            :disabled="isAnyOperationInProgress"
            class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
            @click="onClearCustomerPricingClick">
            <span>
              {{
                isClearingCustomerPricing
                  ? $t('W: Rydder kundepriser...')
                  : $t('W: Ryd kundepriser')
              }}
            </span>
            <IconLucideTrash2 class="h-4 w-4" />
          </button>
          <button
            :disabled="isAnyOperationInProgress"
            class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
            @click="onClearAllClick">
            <span>
              {{ isClearing ? $t('W: Rydder...') : $t('W: Ryd alt') }}
            </span>
            <IconLucideTrash2 class="h-4 w-4" />
          </button>
        </template>
        <button
          v-if="hasReportTemplates"
          :disabled="isClearingReportTemplates"
          class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
          @click="onClearReportTemplatesClick">
          <span>
            {{ isClearingReportTemplates ? $t('W: Rydder...') : $t('W: Ryd skabeloner') }}
          </span>
          <IconLucideTrash2 class="h-4 w-4" />
        </button>
        <button
          v-if="hasBilagLibraryItems"
          :disabled="isClearingBilagLibraryItems"
          class="mt-1 flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-action hover:bg-action-soft-bg disabled:cursor-not-allowed disabled:opacity-50"
          @click="onClearBilagLibraryItemsClick">
          <span>
            {{ isClearingBilagLibraryItems ? $t('W: Rydder...') : $t('W: Ryd alle bilag') }}
          </span>
          <IconLucideTrash2 class="h-4 w-4" />
        </button>
      </div>
    </div>
    <MenuDivider v-if="isDeveloper" />
    <MenuOption
      id="shortcuts"
      :title="$t('W: Tastaturgenveje')"
      @select="onMenuSelect">
      <template #icon>
        <IconLucideKeyboard
          class="-mt-1 h-4 w-4"
          aria-hidden="true" />
      </template>
    </MenuOption>
    <MenuOption
      id="logout"
      :title="$t('action.log-out')"
      @select="onMenuSelect">
      <template #icon>
        <IconLucideLogOut
          class="-mt-1 h-4 w-4"
          aria-hidden="true" />
      </template>
    </MenuOption>
  </div>
</template>

<script lang="ts">
import { EMU } from '@/globals/typescript/utils/host'
import { useAuthStore } from '@/stores/authStore'
import { useModalStore } from '@/stores/modalStore'
import { useUserStore } from '@/stores/userStore'
import { useAccountStore } from '@/stores/accountStore'
import { useDevSeedStore } from '@/stores/devSeedStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useBilagLibraryItemStore } from '@/stores/bilagLibraryItemStore'
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'

export default {
  name: 'HeaderUserMenu',
  emits: ['close'],
  data() {
    return {
      isClearingReportTemplates: false,
      isClearingBilagLibraryItems: false,
      EMU,
    }
  },
  computed: {
    ...mapState(useUserStore, [
      'currentUser',
      'isLabAdminUser',
      'isDeveloper',
      'showDevActions',
    ]),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useDevSeedStore, [
      'isSeeding',
      'isSeedingOrders',
      'isClearing',
      'isClearingOrders',
      'isAnyOperationInProgress',
      'isCatalogEmpty',
      'hasOrders',
      'canSeedOrders',
      'canSeedCustomerPricing',
      'isSeedingCustomerPricing',
      'isClearingCustomerPricing',
    ]),
    ...mapState(useReportTemplateStore, ['hasReportTemplates']),
    ...mapState(useBilagLibraryItemStore, ['hasBilagLibraryItems']),
  },
  methods: {
    ...mapActions(useUserStore, [
      'canUser',
      'toggleShowDevActions',
    ]),
    ...mapActions(useAuthStore, ['logout']),
    ...mapActions(useDevSeedStore, [
      'seedCatalog',
      'seedCustomerPricing',
      'clearCustomerPricing',
      'clearOrders',
      'clearAll',
    ]),
    ...mapActions(useReportTemplateStore, ['deleteAllReportTemplates']),
    ...mapActions(useBilagLibraryItemStore, ['deleteAllBilagLibraryItems']),
    onMenuSelect(id: string) {
      if (id === 'logout') {
        this.$emit('close')
        this.logout()
      }

      if (id === 'profile') {
        this.$emit('close')
        const modal = useModalStore()
        modal.open({ name: 'UserProfileModal', width: 'md' })
      }

      if (id === 'update-pin') {
        this.$emit('close')
        const modal = useModalStore()
        modal.open({ name: 'UpdatePinCodeModal', width: 'sm' })
      }

      if (id === 'invite') {
        this.$emit('close')
        const modal = useModalStore()
        modal.open({ name: 'InviteModal' })
      }

      if (id === 'shortcuts') {
        this.$emit('close')
        const modal = useModalStore()
        modal.open({ name: 'KeyboardShortcutsModal', width: 'sm' })
      }
    },

    onSeedCatalogClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Seed katalog'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Skriver analyser, prøvetyper, analysepakker, tekstlister, TAT-tider, tillægsydelser, lister og beskrivelser og prisklasser til Firestore.'
            ),
            this.$t(
              'W: Eksisterende data overskrives ikke — kør "Ryd katalog" først for at starte fra bunden.'
            ),
          ],
          onConfirm: () => this.runSeedCatalog(),
        },
      })
    },

    async runSeedCatalog(): Promise<void> {
      const { success, error } = useToast()
      try {
        await this.seedCatalog()
        success(this.$t('W: Katalog seedet'))
      } catch {
        error(this.$t('W: Noget gik galt under seeding af katalog'))
      }
    },

    onSeedOrdersClick(): void {
      const modal = useModalStore()
      modal.open({ name: 'SeedOrdersModal', width: 'sm' })
    },

    onSeedCustomerPricingClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Seed kundepriser'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Giver én erhvervskunde, én forsikringskunde og én privatkunde prisoverstyringer oven på det første aktive prissegment — alle tilstande på fanerne Analyser, Svartider og Tillægsydelser bliver dækket.'
            ),
            this.$t(
              'W: Overskriver eksisterende overstyringer for de valgte kunder.'
            ),
          ],
          onConfirm: () => this.runSeedCustomerPricing(),
        },
      })
    },

    async runSeedCustomerPricing(): Promise<void> {
      const { success, error } = useToast()
      try {
        const result = await this.seedCustomerPricing()
        if (!result.seeded.length) {
          error(this.$t('W: Ingen kunder at seede priser for'))
          return
        }
        success(
          this.$t('W: Kundepriser seedet for {names}', {
            names: result.seeded.map((x) => x.name).join(', '),
          })
        )
      } catch {
        error(this.$t('W: Noget gik galt under seeding af kundepriser'))
      }
    },

    onClearCustomerPricingClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Ryd kundepriser'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Fjerner alle prisoverstyringer på analyser, svartider og tillægsydelser for alle kunder. Prissegmentet bevares. Handlingen kan ikke fortrydes.'
            ),
          ],
          onConfirm: () => this.runClearCustomerPricing(),
        },
      })
    },

    async runClearCustomerPricing(): Promise<void> {
      const { success, error } = useToast()
      try {
        const cleared = await this.clearCustomerPricing()
        success(
          cleared === 1
            ? this.$t('W: 1 kundes priser ryddet')
            : this.$t('W: {count} kunders priser ryddet', { count: cleared })
        )
      } catch {
        error(this.$t('W: Noget gik galt under rydning af kundepriser'))
      }
    },

    onClearOrdersClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Ryd ordrer'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Sletter alle ordrer og prøver for denne konto. Handlingen kan ikke fortrydes.'
            ),
          ],
          onConfirm: () => this.runClearOrders(),
        },
      })
    },

    async runClearOrders(): Promise<void> {
      const { success, error } = useToast()
      try {
        await this.clearOrders()
        success(this.$t('W: Ordrer ryddet'))
      } catch {
        error(this.$t('W: Noget gik galt under sletning af ordrer'))
      }
    },

    onClearAllClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Ryd katalog'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Sletter alle analyser, prøvetyper, analysepakker, tekstlister, TAT-tider, tillægsydelser, lister og beskrivelser, prisklasser og ordrer for denne konto.'
            ),
            this.$t(
              'W: Kundernes prisklasse nulstilles. Handlingen kan ikke fortrydes.'
            ),
          ],
          onConfirm: () => this.runClear(),
        },
      })
    },

    async runClear(): Promise<void> {
      const { success, error } = useToast()
      try {
        await this.clearAll()
        success(this.$t('W: Katalog ryddet'))
      } catch {
        error(this.$t('W: Noget gik galt under rydning'))
      }
    },

    onClearReportTemplatesClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Ryd skabeloner'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Sletter alle rapportskabeloner for denne konto. Handlingen kan ikke fortrydes.'
            ),
          ],
          onConfirm: () => this.runClearReportTemplates(),
        },
      })
    },

    async runClearReportTemplates(): Promise<void> {
      const { success, error } = useToast()
      this.isClearingReportTemplates = true
      try {
        await this.deleteAllReportTemplates()
        success(this.$t('W: Skabeloner ryddet'))
      } catch {
        error(this.$t('W: Noget gik galt under rydning af skabeloner'))
      } finally {
        this.isClearingReportTemplates = false
      }
    },

    onClearBilagLibraryItemsClick(): void {
      const modal = useModalStore()
      modal.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Ryd alle bilag'),
          textLines: [
            this.$t('W: Kun for kontoen:'),
            { text: this.labAccount?.name ?? '', bold: true },
            this.$t(
              'W: Sletter alle bilag i bilagsbiblioteket for denne konto, inklusiv de tilhørende filer. Handlingen kan ikke fortrydes.'
            ),
          ],
          onConfirm: () => this.runClearBilagLibraryItems(),
        },
      })
    },

    async runClearBilagLibraryItems(): Promise<void> {
      const { success, error } = useToast()
      this.isClearingBilagLibraryItems = true
      try {
        await this.deleteAllBilagLibraryItems()
        success(this.$t('W: Bilag ryddet'))
      } catch {
        error(this.$t('W: Noget gik galt under rydning af bilag'))
      } finally {
        this.isClearingBilagLibraryItems = false
      }
    },
  },
}
</script>

